import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';

import { Button } from 'shared/components/Button';
import { Modal } from 'shared/components/Modal';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { OrderSummary } from 'checkout/components/Summary/OrderSummary';
import { useCurrency } from 'shared/hooks/useCurrency';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { t } from 'localization/utils/t';

const TP = 'checkout.components.Payment.ExpressPayModal';

export const ExpressPayModal = () => {
  const { selectedCurrencyCode } = useCurrency();
  const { user, isAuthenticated } = useStoreSelector((state) => state.auth);

  const {
    addresses,
    applePayAvailable,
    dismissExpressPayModal,
    ExpressPayButton,
    isExpressPayModalVisible,
    order,
  } = useCheckoutContext();

  const shippingAddresses = Object.values(addresses).filter(
    (address) => address.addressType === 'shipping',
  );

  const handleTrackerClick = () => {
    sendTrackingEvent('EXPRESS_CHECKOUT_TAP', {
      box_condition: order?.product?.boxCondition,
      condition_category: order?.product?.shoeCondition,
      currency_code: selectedCurrencyCode,
      instant_ship: order?.isInstantShip,
      price: order?.localizedPriceCents?.amount,
      product_template_slug: order?.product?.productTemplate?.slug,
      shoe_condition: order?.product?.shoeCondition,
      size: order?.product?.size,
    });
  };

  // NOTE: Only display express pay modal for first time authenticated checkout users
  if (
    applePayAvailable &&
    isEmpty(shippingAddresses) &&
    isExpressPayModalVisible &&
    isAuthenticated &&
    !user?.isGuest
  ) {
    return (
      <ExpressPayModal.Modal
        data-qa="checkout_express_pay_modal"
        onClose={dismissExpressPayModal}
        title={t(`${TP}.yourOrder`, 'Your Order')}
      >
        <ExpressPayModal.OrderSummary
          showPriceBreakdown={false}
          showSupportLinks={false}
          showTitle={false}
          isModal
        />
        <ExpressPayModal.Background>
          <ExpressPayButton
            data-qa="checkout_apple_pay_express_modal_payment_submit"
            onTrackerClick={handleTrackerClick}
          />
        </ExpressPayModal.Background>

        <ExpressPayModal.Button
          data-qa="checkout_express_pay_modal_continue_button"
          inverted
          onClick={dismissExpressPayModal}
          title={t(`${TP}.continueToCheckout`, 'Continue To Checkout')}
        />
      </ExpressPayModal.Modal>
    );
  }

  return null;
};

ExpressPayModal.Modal = styled(Modal)`
  min-width: 90vw;
  padding: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    min-width: 400px;
  }

  button {
    padding: 0;
  }
`;

ExpressPayModal.OrderSummary = styled(OrderSummary)`
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
  padding-bottom: calc(32px - 1rem);
`;

ExpressPayModal.Button = styled(Button)`
  margin-top: 8px;
`;

ExpressPayModal.Background = styled.div`
  background-color: ${({ theme }) => theme.colors.black};
  height: 42px;

  > div {
    padding-top: 5px;
  }
`;
